import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {
    ContentCopyRounded,
    DownloadRounded,
    ExpandMoreRounded,
    VpnKeyRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import { Button, IconButton } from "../components/Buttons";
import { getApiDocs, getOpenApiSpec } from "../api";
import { FIELD_TYPES, apiError } from "../utils/meta";
import { getOrgName } from "../utils/session";

const MONO = "'SF Mono', Menlo, monospace";

const LABEL_SX = {
    fontSize: 11,
    letterSpacing: "2px",
    fontWeight: 700,
    color: "var(--text-tertiary)",
};

/* Verb colours — GET stays neutral, the writes carry weight. */
const METHOD_COLORS = {
    GET: "var(--text-secondary)",
    POST: "#1E6B4F",
    PUT: "#C4A24E",
    DELETE: "#B3423A",
};

const methodColor = (method) =>
    METHOD_COLORS[String(method ?? "").toUpperCase()] ??
    "var(--text-secondary)";

const typeLabel = (type) =>
    FIELD_TYPES.find((t) => t.key === type)?.label ?? type;

/** `Acme Realty` → `acme-realty`, for the downloaded spec's filename. */
const fileSlug = (name) =>
    (name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "tessera";

async function copyToClipboard(text, label) {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
    } catch {
        toast.error("Could not copy — select and copy it manually");
    }
}

/* ------------------------------------------------------------------ */
/* Dark terminal-style code block                                       */
/* ------------------------------------------------------------------ */

function CodeBlock({ title = "terminal", code, copyLabel, children }) {
    return (
        <Stack
            sx={{
                background: "#171715",
                border: "1px solid #383632",
                borderRadius: "6px",
                overflow: "hidden",
            }}
        >
            <Stack
                sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1,
                    p: "8px 12px 8px 16px",
                    borderBottom: "1px solid #383632",
                }}
            >
                <Stack sx={{ flexDirection: "row", gap: "7px" }}>
                    {[0, 1, 2].map((i) => (
                        <Stack
                            key={i}
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: "#4a4742",
                            }}
                        />
                    ))}
                </Stack>
                <Typography
                    sx={{ fontFamily: MONO, fontSize: 12, color: "#8b8781" }}
                >
                    {title}
                </Typography>
                {code && (
                    <Stack sx={{ flex: 1, alignItems: "flex-end" }}>
                        <IconButton
                            size="28px"
                            variant="text"
                            title="Copy"
                            onClick={() =>
                                copyToClipboard(code, copyLabel ?? "Snippet")
                            }
                            sx={{ color: "#8b8781" }}
                        >
                            <ContentCopyRounded sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Stack>
                )}
            </Stack>
            <Typography
                component="pre"
                sx={{
                    m: 0,
                    p: "18px 22px",
                    fontFamily: MONO,
                    fontSize: 13,
                    lineHeight: 1.9,
                    color: "#d8d5cf",
                    whiteSpace: "pre",
                    overflowX: "auto",
                }}
            >
                {children ?? code}
            </Typography>
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* Fields table                                                         */
/* ------------------------------------------------------------------ */

function FieldsTable({ fields }) {
    if (fields.length === 0) {
        return (
            <Typography
                sx={{ fontSize: 13.5, color: "var(--text-secondary)" }}
            >
                No fields yet — the record payload is empty until you add some.
            </Typography>
        );
    }

    return (
        <TableContainer sx={{ background: "var(--bg-secondary)" }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Key</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Options</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fields.map((f) => (
                        <TableRow key={f.key}>
                            <TableCell
                                sx={{
                                    fontFamily: MONO,
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                }}
                            >
                                {f.key}
                            </TableCell>
                            <TableCell>{f.label}</TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    label={typeLabel(f.type)}
                                    sx={{
                                        height: 20,
                                        fontSize: 11,
                                        background: "var(--bg-tint-soft)",
                                        border: "1px solid var(--border-mid)",
                                        color: "var(--text-secondary)",
                                    }}
                                />
                            </TableCell>
                            <TableCell>
                                {f.required ? (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: "var(--error)",
                                        }}
                                    >
                                        required
                                    </Typography>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: "var(--text-tertiary)",
                                        }}
                                    >
                                        optional
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                {(f.options ?? []).length > 0 ? (
                                    <Stack
                                        sx={{
                                            flexDirection: "row",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                        }}
                                    >
                                        {f.options.map((opt) => (
                                            <Chip
                                                key={opt}
                                                size="small"
                                                label={opt}
                                                sx={{
                                                    height: 20,
                                                    fontSize: 11,
                                                    background:
                                                        "var(--bg-tint)",
                                                    border: "1px solid var(--border)",
                                                    color: "var(--text-strong-muted)",
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: "var(--text-tertiary)",
                                        }}
                                    >
                                        —
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/* ------------------------------------------------------------------ */
/* Endpoint list                                                        */
/* ------------------------------------------------------------------ */

function EndpointRow({ endpoint }) {
    const color = methodColor(endpoint.method);
    return (
        <Stack
            sx={{
                border: "1px solid var(--border-mid)",
                borderRadius: "4px",
                background: "var(--bg-secondary)",
                p: "12px 14px",
                gap: 0.75,
            }}
        >
            <Stack
                sx={{
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 1.25,
                }}
            >
                <Chip
                    size="small"
                    label={endpoint.method}
                    sx={{
                        height: 22,
                        minWidth: 62,
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: MONO,
                        color,
                        background: "transparent",
                        border: `1px solid ${color}`,
                    }}
                />
                <Typography
                    sx={{
                        fontFamily: MONO,
                        fontSize: 13,
                        wordBreak: "break-all",
                    }}
                >
                    {endpoint.path}
                </Typography>
            </Stack>

            <Typography
                sx={{ fontSize: 13.5, color: "var(--text-secondary)" }}
            >
                {endpoint.summary}
            </Typography>

            {endpoint.query && (
                <Typography
                    sx={{ fontSize: 12.5, color: "var(--text-tertiary)" }}
                >
                    Query:{" "}
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: MONO,
                            fontSize: 12.5,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        {endpoint.query}
                    </Typography>
                </Typography>
            )}

            {endpoint.body && (
                <Typography
                    component="pre"
                    sx={{
                        m: 0,
                        mt: 0.25,
                        p: "10px 12px",
                        background: "var(--bg-tint-soft)",
                        border: "1px solid var(--border)",
                        borderRadius: "3px",
                        fontFamily: MONO,
                        fontSize: 12,
                        lineHeight: 1.7,
                        color: "var(--text-strong-muted)",
                        whiteSpace: "pre",
                        overflowX: "auto",
                    }}
                >
                    {JSON.stringify(endpoint.body, null, 2)}
                </Typography>
            )}
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* One object's collapsible section                                     */
/* ------------------------------------------------------------------ */

function ObjectSection({ object, expanded, onToggle }) {
    const fields = object.fields ?? [];
    const endpoints = object.endpoints ?? [];

    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            elevation={0}
            square={false}
            sx={{
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-mid)",
                borderRadius: "6px",
                backgroundImage: "none",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreRounded
                        sx={{ fontSize: 20, color: "var(--text-secondary)" }}
                    />
                }
                sx={{ p: "6px 20px", "& .MuiAccordionSummary-content": { gap: 1.5 } }}
            >
                <Stack
                    sx={{
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 1.5,
                        flex: 1,
                    }}
                >
                    <Typography variant="h6" sx={{ fontFamily: "'Newsreader', Georgia, serif", fontWeight: 500 }}>
                        {object.name}
                    </Typography>
                    <Chip
                        size="small"
                        label={object.slug}
                        sx={{
                            height: 22,
                            fontFamily: MONO,
                            fontSize: 11.5,
                            background: "var(--bg-tint-soft)",
                            border: "1px solid var(--border-mid)",
                            color: "var(--text-secondary)",
                        }}
                    />
                    <Typography
                        sx={{ fontSize: 12.5, color: "var(--text-tertiary)" }}
                    >
                        {fields.length} field{fields.length === 1 ? "" : "s"} ·{" "}
                        {endpoints.length} endpoint
                        {endpoints.length === 1 ? "" : "s"}
                    </Typography>
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ p: "4px 20px 22px", gap: 2.5, display: "flex", flexDirection: "column" }}>
                <Stack sx={{ gap: 1 }}>
                    <Typography sx={LABEL_SX}>FIELDS</Typography>
                    <FieldsTable fields={fields} />
                </Stack>

                <Stack sx={{ gap: 1 }}>
                    <Typography sx={LABEL_SX}>ENDPOINTS</Typography>
                    <Stack sx={{ gap: 1 }}>
                        {endpoints.map((ep) => (
                            <EndpointRow
                                key={`${ep.method} ${ep.path}`}
                                endpoint={ep}
                            />
                        ))}
                    </Stack>
                </Stack>

                {object.curl && (
                    <Stack sx={{ gap: 1 }}>
                        <Typography sx={LABEL_SX}>TRY IT</Typography>
                        <CodeBlock
                            title={`${object.slug} — curl`}
                            code={object.curl}
                            copyLabel="curl example"
                        />
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminApiDocs() {
    const [docs, setDocs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await getApiDocs();
            setDocs(data ?? null);
            setExpanded(data?.objects?.[0]?.id ?? null);
        } catch (err) {
            const message = apiError(err, "Failed to load the API docs");
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function handleDownload() {
        setDownloading(true);
        try {
            const { data } = await getOpenApiSpec();
            const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${fileSlug(getOrgName())}-openapi.json`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            toast.success("OpenAPI spec downloaded");
        } catch (err) {
            toast.error(apiError(err, "Could not download the spec"));
        } finally {
            setDownloading(false);
        }
    }

    const baseUrl = docs?.baseUrl ?? "https://your-host";
    const authHeader = docs?.auth?.header ?? "x-api-key";
    const objects = docs?.objects ?? [];
    const events = docs?.webhooks?.events ?? [];

    return (
        <Stack>
            <PageHeader
                title="API documentation"
                subtitle="Generated from this workspace's own objects and fields — every object you build gets a documented REST API"
                action={
                    <Stack sx={{ flexDirection: "row", gap: 1.5 }}>
                        <Button
                            variant="soft-outlined"
                            startIcon={<DownloadRounded />}
                            loading={downloading}
                            onClick={handleDownload}
                        >
                            Download OpenAPI
                        </Button>
                        <Button
                            component={Link}
                            to="/admin/api-keys"
                            variant="contained"
                            color="primary"
                            startIcon={<VpnKeyRounded />}
                            sx={{ textDecoration: "none" }}
                        >
                            Manage API keys
                        </Button>
                    </Stack>
                }
            />

            {loading && (
                <Stack sx={{ alignItems: "center", py: 8 }}>
                    <CircularProgress size={28} />
                </Stack>
            )}

            {!loading && error && (
                <Stack
                    sx={{
                        alignItems: "flex-start",
                        gap: 1.5,
                        border: "1px solid var(--border-mid)",
                        borderRadius: "6px",
                        background: "var(--bg-secondary)",
                        p: "22px 24px",
                    }}
                >
                    <Typography sx={{ fontSize: 14.5, color: "var(--error)" }}>
                        {error}
                    </Typography>
                    <Button variant="soft-outlined" onClick={load}>
                        Try again
                    </Button>
                </Stack>
            )}

            {!loading && !error && (
                <Stack sx={{ gap: 3 }}>
                    {/* Authentication */}
                    <Stack sx={{ gap: 1.25, maxWidth: 860 }}>
                        <Typography sx={LABEL_SX}>AUTHENTICATION</Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: "var(--text-secondary)",
                            }}
                        >
                            Send your key in the{" "}
                            <code>{authHeader}</code> header on every request.{" "}
                            {docs?.auth?.note ??
                                "A key acts with the permissions of the role it is bound to."}{" "}
                            There is no separate API scope — if the role can't
                            read an object, neither can the key.
                        </Typography>
                        <CodeBlock
                            code={`curl -H "${authHeader}: tessera_live_..." \\\n  ${baseUrl}/api/crm/o/{object}/records`}
                            copyLabel="curl example"
                        >
                            {`curl -H "${authHeader}: `}
                            <Typography
                                component="span"
                                sx={{ color: "#4caf87", fontFamily: MONO }}
                            >
                                tessera_live_…
                            </Typography>
                            {`" \\\n  ${baseUrl}/api/crm/o/`}
                            <Typography
                                component="span"
                                sx={{ color: "#8b8781", fontFamily: MONO }}
                            >
                                {"{object}"}
                            </Typography>
                            {"/records"}
                        </CodeBlock>
                        <Typography
                            sx={{ fontSize: 13, color: "var(--text-tertiary)" }}
                        >
                            Base URL{" "}
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: MONO,
                                    fontSize: 13,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                {baseUrl}
                            </Typography>
                        </Typography>
                    </Stack>

                    {/* Per-object reference */}
                    {objects.length === 0 ? (
                        <Stack
                            sx={{
                                alignItems: "center",
                                gap: 2,
                                border: "1px solid var(--border-mid)",
                                borderRadius: "6px",
                                background: "var(--bg-secondary)",
                                p: "48px 24px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 15,
                                    color: "var(--text-secondary)",
                                    textAlign: "center",
                                }}
                            >
                                Create an object first — your API appears here
                                automatically.
                            </Typography>
                            <Button
                                component={Link}
                                to="/admin/objects"
                                variant="contained"
                                color="primary"
                                sx={{ textDecoration: "none" }}
                            >
                                Go to Objects
                            </Button>
                        </Stack>
                    ) : (
                        <Stack sx={{ gap: 1.5 }}>
                            <Typography sx={LABEL_SX}>
                                YOUR OBJECTS
                            </Typography>
                            {objects.map((object) => (
                                <ObjectSection
                                    key={object.id}
                                    object={object}
                                    expanded={expanded === object.id}
                                    onToggle={() =>
                                        setExpanded((prev) =>
                                            prev === object.id
                                                ? null
                                                : object.id,
                                        )
                                    }
                                />
                            ))}
                        </Stack>
                    )}

                    {/* Webhooks */}
                    <Stack
                        sx={{
                            gap: 1.25,
                            maxWidth: 860,
                            border: "1px solid var(--border-mid)",
                            borderRadius: "6px",
                            background: "var(--bg-secondary)",
                            p: "20px 22px",
                        }}
                    >
                        <Typography sx={LABEL_SX}>WEBHOOKS</Typography>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                flexWrap: "wrap",
                                gap: 0.75,
                            }}
                        >
                            {events.length === 0 && (
                                <Typography
                                    sx={{
                                        fontSize: 13.5,
                                        color: "var(--text-tertiary)",
                                    }}
                                >
                                    No events published yet.
                                </Typography>
                            )}
                            {events.map((ev) => (
                                <Chip
                                    key={ev}
                                    size="small"
                                    label={ev}
                                    sx={{
                                        height: 22,
                                        fontFamily: MONO,
                                        fontSize: 11.5,
                                        background: "var(--bg-tint-soft)",
                                        border: "1px solid var(--border-mid)",
                                        color: "var(--text-secondary)",
                                    }}
                                />
                            ))}
                        </Stack>
                        {docs?.webhooks?.signature && (
                            <Typography
                                sx={{
                                    fontSize: 13.5,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                {docs.webhooks.signature}
                            </Typography>
                        )}
                        <Typography
                            component={Link}
                            to="/admin/webhooks"
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--accent)",
                                textDecoration: "none",
                                alignSelf: "flex-start",
                            }}
                        >
                            Manage webhooks →
                        </Typography>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}
